import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { relTime, fmtDate } from "@/lib/orgUtils";
import { Monitor, ShieldOff } from "lucide-react";

export default function SecurityDevices() {
  const { user } = useAuth();
  const [devices, setDevices] = useState([]);
  const [scope, setScope] = useState("mine");
  const load = useCallback(() => {
    const path = scope === "all" && ["super_admin", "admin"].includes(user.role) ? "/devices/all" : "/devices";
    api.get(path).then((r) => setDevices(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [scope, user.role]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-black text-lg">Trusted devices</h3>
          <p className="text-sm text-[#676a82] font-medium">Devices that can sign in without OTP re-verification.</p>
        </div>
        <div className="flex gap-2">
          {["super_admin", "admin"].includes(user.role) && (
            <button data-testid="device-scope-toggle" onClick={() => setScope(scope === "mine" ? "all" : "mine")} className="px-3 h-9 rounded-xl bg-white border border-[#181B34]/10 text-xs font-bold">
              {scope === "mine" ? "Show all users" : "Show mine only"}
            </button>
          )}
          <button data-testid="revoke-all-devices" onClick={() => api.post("/devices/revoke-others").then(() => { toast.success("All your devices revoked"); load(); })}
            className="flex items-center gap-1.5 px-3 h-9 rounded-xl bg-[#E2445C]/10 text-[#E2445C] text-xs font-bold"><ShieldOff size={13} /> Revoke all mine</button>
        </div>
      </div>
      <div className="mt-4 space-y-2">
        {devices.map((d) => {
          const expired = d.expires_at < new Date().toISOString();
          return (
            <div key={d.id} className="flex items-center gap-3 p-3.5 rounded-xl bg-white border border-[#181B34]/10" data-testid={`device-${d.id}`}>
              <Monitor size={18} className="text-[#6161FF]" />
              <div className="flex-1">
                <p className="font-bold text-sm">{d.label} {scope === "all" && <span className="text-xs font-semibold text-[#9699AD]">· {d.user_email}</span>}</p>
                <p className="text-xs font-semibold text-[#9699AD]">Added {fmtDate(d.added_at)} · Last used {relTime(d.last_used_at)}</p>
              </div>
              {expired && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#181B34]/8 text-[#676a82]">Expired</span>}
              <button data-testid={`revoke-device-${d.id}`} onClick={() => api.delete(`/devices/${d.id}`).then(() => { toast.success("Device revoked"); load(); })}
                className="text-xs font-bold text-[#E2445C] hover:underline">Revoke</button>
            </div>
          );
        })}
        {devices.length === 0 && <p className="p-6 text-center text-[#676a82] font-medium bg-white rounded-xl border border-[#181B34]/10">No trusted devices.</p>}
      </div>
      <p className="mt-4 text-xs text-[#9699AD] font-medium">Security tip: revoke any device you don't recognize immediately.</p>
    </div>
  );
}
